import { useEffect, useRef, useState } from "react";

import {
    getAppUrls,
    IAppUrls,
    parseNamespace,
} from "@formant/core/common/app-urls";
import { namespace } from "@formant/core/common/config";
import { fork } from "@formant/core/common/fork";
import { IShare } from "@formant/core/model/IShare";

import { authStore, organizationStore } from "../../stores";
import {
    coherenceShareRedirectUrl,
    coherenceUrlFor,
    fetchCoherenceRedirectEnabled,
    ILocationLike,
    isCoherenceShare,
    legacyPreferenceFromSearch,
    shouldRedirectToCoherence,
} from "../coherenceRedirect/coherenceRedirect";

const keepLegacyStorageKey = "formant.useLegacy";

function appUrlsForNamespace(): IAppUrls | undefined {
    try {
        return getAppUrls(parseNamespace(namespace));
    } catch {
        return undefined;
    }
}

// sessionStorage throws in some private-browsing modes; treat that as "no
// preference" rather than failing the page.
function readKeepLegacy(): boolean {
    try {
        return sessionStorage.getItem(keepLegacyStorageKey) === "true";
    } catch {
        return false;
    }
}

function writeKeepLegacy(keepLegacy: boolean): void {
    try {
        if (keepLegacy) {
            sessionStorage.setItem(keepLegacyStorageKey, "true");
        } else {
            sessionStorage.removeItem(keepLegacyStorageKey);
        }
    } catch {
        // Nothing to persist to; the choice lasts for this page load only.
    }
}

async function redirectIfEnabled(
    { coherence, platform }: IAppUrls,
    landing: ILocationLike,
): Promise<void> {
    const redirect = await shouldRedirectToCoherence({
        locations: [landing, window.location],
        keepLegacy: readKeepLegacy(),
        readFlag: async () =>
            fetchCoherenceRedirectEnabled(platform, await authStore.token()),
    });
    if (redirect) {
        // replace() so the back button doesn't return to a page that would
        // redirect again.
        window.location.replace(coherenceUrlFor(coherence, landing));
    }
}

/**
 * Sends signed-in users of an organization with `coherenceRedirectEnabled` to
 * Coherence. A device or alert link opens that device there; any other page
 * opens Coherence's home. The session carries over through the shared
 * `.formant.io` auth cookie. `?useLegacy=true` keeps the tab on app.formant;
 * `?useLegacy=false` clears that choice.
 */
export function useCoherenceRedirect(): void {
    const organization = organizationStore.authenticated();
    // The router rewrites the URL once it takes over, so keep the URL the user
    // landed on for the escape hatch, the exemption checks and the target page.
    const landing = useRef<ILocationLike>({
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
    });
    const checked = useRef(false);

    useEffect(() => {
        const keepLegacy = legacyPreferenceFromSearch(landing.current.search);
        if (keepLegacy !== undefined) {
            writeKeepLegacy(keepLegacy);
        }
    }, []);

    useEffect(() => {
        const appUrls = appUrlsForNamespace();
        if (checked.current || !organization || !appUrls) {
            return;
        }
        checked.current = true;
        fork(redirectIfEnabled(appUrls, landing.current));
    }, [organization]);
}

function shareKeepsLegacy(): boolean {
    return (
        legacyPreferenceFromSearch(window.location.search) ?? readKeepLegacy()
    );
}

/** Resolves true when the browser is leaving for Coherence. */
async function redirectShareIfEnabled(
    { coherence, platform }: IAppUrls,
    share: IShare,
): Promise<boolean> {
    const target = await coherenceShareRedirectUrl({
        coherenceUrl: coherence,
        share,
        keepLegacy: shareKeepsLegacy(),
        readFlag: async () =>
            fetchCoherenceRedirectEnabled(platform, await authStore.token()),
    });
    if (!target) {
        return false;
    }
    window.location.replace(target);
    return true;
}

/**
 * "checking" while the flag is read, and while the browser leaves for
 * Coherence; "stay" once the share opens here.
 */
export type ShareRedirectDecision = "checking" | "stay";

/**
 * Sends a share link to Coherence's share page when the sharing organization
 * has `coherenceRedirectEnabled`. Used on the share page once the share has
 * loaded: by then the page has signed in with the share's code, so the flag
 * is the sharing organization's even for a viewer signed in elsewhere.
 */
export function useShareCoherenceRedirect(
    share: IShare,
    { enabled = true }: { enabled?: boolean } = {},
): ShareRedirectDecision {
    // Shares that can't move show straight away; the rest wait for the flag.
    const [decision, setDecision] = useState<ShareRedirectDecision>(() =>
        enabled &&
        appUrlsForNamespace() &&
        isCoherenceShare(share) &&
        !shareKeepsLegacy()
            ? "checking"
            : "stay",
    );
    // The share store can hand back a new copy of the same share; one check
    // per page is enough.
    const checked = useRef(decision === "stay");

    useEffect(() => {
        const appUrls = appUrlsForNamespace();
        if (checked.current || !appUrls) {
            return;
        }
        checked.current = true;
        fork(
            (async () => {
                let leaving = false;
                try {
                    leaving = await redirectShareIfEnabled(appUrls, share);
                } finally {
                    // Never leave the page blank: anything but a redirect shows it.
                    if (!leaving) {
                        setDecision("stay");
                    }
                }
            })(),
        );
    }, [share]);

    return decision;
}
